// frontend/src/stores/auth.js - Enhanced with FORCED Location Tracking for Employees
import { defineStore } from 'pinia';
import axios from 'axios';
import { STORAGE_KEYS } from '@/utils/constants';
import locationService from '@/services/locationService'; // Import the new location service

// Configure axios defaults
const API_BASE_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
axios.defaults.baseURL = API_BASE_URL;

export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: null,
    user: null,
    isAuthenticated: false,
    isInitializing: true,
    loading: false,
    error: null,
    lastActivity: null,
    sessionTimeout: 24 * 60 * 60 * 1000, // 24 hours for employees
    adminSessionTimeout: 8 * 60 * 60 * 1000, // 8 hours for admins
    sessionCheckInterval: null,
    sessionWarningTimer: null,
    showSessionWarning: false,
    sessionWarningTimeout: 5 * 60 * 1000, // Show warning 5 minutes before expiry

    // Location-related state
    locationStatus: {
      isEnabled: false,
      isTracking: false,
      hasPermission: false,
      lastKnownLocation: null,
      error: null,
    },

    // Location policy (company-wide forced tracking)
    locationPolicy: {
      mandatoryForEmployees: true, // Force tracking for all employees
      requireHighAccuracy: true, // Force high accuracy GPS
      updateFrequency: 30000, // 30 second updates
      allowEmployeeControl: false, // Employees cannot disable
      adminCanDisable: true, // Admins can control their own
    },

    // Critical location failure state
    showCriticalLocationError: false,
    showLocationRequired: false,
    locationFailureCount: 0,
  }),

  getters: {
    isAdmin: state => state.user?.role === 'admin',
    isEmployee: state => state.user?.role === 'employee',
    userRole: state => state.user?.role || null,
    isSessionValid: state => {
      if (!state.lastActivity || !state.user) return false;
      const timeout =
        state.user.role === 'admin'
          ? state.adminSessionTimeout
          : state.sessionTimeout;
      return Date.now() - state.lastActivity < timeout;
    },
    userName: state => state.user?.name || '',
    userEmail: state => state.user?.email || '',
    userDepartment: state => state.user?.department || '',
    employeeId: state => state.user?.employee_id || state.user?.id || '',
    canWorkField: state => state.user?.can_work_field || false,
    requiresFieldApproval: state =>
      state.user?.requires_field_approval || false,

    // Session timeout calculations
    timeUntilSessionExpiry: state => {
      if (!state.lastActivity) return 0;

      const timeout =
        state.user?.role === 'admin'
          ? state.adminSessionTimeout
          : state.sessionTimeout;
      const expiryTime = state.lastActivity + timeout;
      const remaining = expiryTime - Date.now();

      return Math.max(0, remaining);
    },

    isSessionExpiring: state => {
      const timeLeft = state.timeUntilSessionExpiry;
      return timeLeft > 0 && timeLeft <= 5 * 60 * 1000; // 5 minutes
    },

    // Location getters
    currentLocationSummary() {
      return locationService.getLocationSummary();
    },

    isLocationAvailable() {
      return (
        this.locationStatus.isEnabled &&
        this.locationStatus.hasPermission &&
        this.locationStatus.lastKnownLocation
      );
    },

    // NEW: Can user control location settings
    canControlLocation: state => {
      // Only admins can control location, employees cannot
      return state.user?.role === 'admin';
    },

    // NEW: Is location mandatory for current user
    isLocationMandatory: state => {
      return (
        state.user?.role === 'employee' &&
        state.locationPolicy.mandatoryForEmployees
      );
    },
  },

  actions: {
    // ============================================================================
    // EXISTING AUTHENTICATION METHODS (ENHANCED)
    // ============================================================================

    // Initialize authentication from storage
    async initializeAuth() {
      try {
        this.isInitializing = true;
        console.log('🔄 Initializing authentication...');

        const token = localStorage.getItem(STORAGE_KEYS.auth.token);
        const userData = localStorage.getItem(STORAGE_KEYS.auth.user);
        const lastActivity = localStorage.getItem(
          STORAGE_KEYS.auth.lastActivity
        );

        if (token && userData) {
          try {
            this.token = token;
            this.user = JSON.parse(userData);
            this.lastActivity = lastActivity
              ? parseInt(lastActivity)
              : Date.now();

            // Set axios authorization header
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

            if (this.isSessionValid) {
              // Verify token with backend
              const isValid = await this.verifyToken();
              if (isValid) {
                this.isAuthenticated = true;
                this.updateActivity();

                // Initialize location in the background so the loading screen
                // clears quickly; EmployeeLocationNotification handles the modal.
                this.initializeLocationService();

                this.startSessionMonitoring();
                console.log('✅ Authentication restored from storage');
              } else {
                console.log('⚠️ Token invalid, clearing authentication');
                this.clearAuth();
              }
            } else {
              console.log('⚠️ Session expired, clearing authentication');
              this.clearAuth();
            }
          } catch (error) {
            console.error('Failed to parse stored auth data:', error);
            this.clearAuth();
          }
        } else {
          console.log('ℹ️ No stored authentication found');
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        this.clearAuth();
      } finally {
        this.isInitializing = false;
      }
    },

    // Verify token with backend
    async verifyToken() {
      try {
        const response = await axios.get('/auth/me');
        if (response.data && response.data.success && response.data.data?.user) {
          const userData = response.data.data.user;
          // Only merge fields that are actually present (non-null) from server —
          // a null name/email from a transient DB failure must not overwrite cached data.
          const patch = Object.fromEntries(
            Object.entries(userData).filter(([, v]) => v !== null && v !== undefined)
          );
          this.user = {
            ...this.user,
            ...patch,
            role: this.user?.role || userData.role || 'employee',
          };
          this.persistAuth();
          return true;
        }
        return false;
      } catch (error) {
        console.error('Token verification failed:', error);
        // Only treat an explicit 401 as invalid — network errors, 500s, etc.
        // should not sign the user out; the backend validates every API call anyway.
        if (error.response?.status === 401) {
          return false;
        }
        return true;
      }
    },

    // Employee login with location context (UNCHANGED)
    async loginEmployee(credentials) {
      try {
        this.loading = true;
        this.error = null;

        console.log('🔑 Attempting employee login with backend...');

        // Get user location for security logging
        let locationData = null;
        try {
          // Try to get location without forcing permission
          const hasPermission = await locationService.getPermissionStatus();
          if (hasPermission === 'granted') {
            locationData = await locationService.getCurrentLocation();
            console.log('📍 Location captured for login:', locationData);
          } else if (navigator.geolocation) {
            // Fallback to basic geolocation
            const position = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                timeout: 5000,
                enableHighAccuracy: false,
              });
            });
            locationData = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracy: position.coords.accuracy,
              timestamp: position.timestamp,
            };
          }
        } catch (error) {
          console.log('⚠️ Location unavailable for login:', error.message);
        }

        const loginPayload = {
          email: credentials.email || credentials.identifier,
          password: credentials.password,
        };

        // Add location data if available
        if (locationData) {
          loginPayload.location = {
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            accuracy: locationData.accuracy,
            timestamp: locationData.timestamp,
          };
        }

        const response = await axios.post('/auth/login/employee', loginPayload);

        if (response.data?.success && response.data?.data?.token && response.data?.data?.user) {
          await this.setAuthenticatedUser(
            response.data.data.token,
            response.data.data.user,
            response.data.data.user.role || 'employee'
          );

          // Always persist auth data to ensure router can access user role
          this.persistAuth();

          console.log('✅ Employee login successful');
          return { success: true, user: response.data.data.user };
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Employee login error:', error);

        let errorMessage = 'Login failed';
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.error = errorMessage;
        return { success: false, error: errorMessage };
      } finally {
        this.loading = false;
      }
    },

    // Admin login with location context (UNCHANGED)
    async loginAdmin(credentials) {
      try {
        this.loading = true;
        this.error = null;

        console.log('🛡️ Attempting admin login with backend...');

        // Get admin location for security logging
        let locationData = null;
        try {
          const hasPermission = await locationService.getPermissionStatus();
          if (hasPermission === 'granted') {
            locationData = await locationService.getCurrentLocation();
            console.log('📍 Admin location captured for login:', locationData);
          }
        } catch (error) {
          console.log(
            '⚠️ Location unavailable for admin login:',
            error.message
          );
        }

        const loginPayload = {
          email: credentials.email || credentials.identifier,
          password: credentials.password,
        };

        // Add location data if available
        if (locationData) {
          loginPayload.location = {
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            accuracy: locationData.accuracy,
            timestamp: locationData.timestamp,
          };
        }

        const response = await axios.post('/auth/admin/login', loginPayload);

        if (response.data?.success && response.data?.data?.token && response.data?.data?.user) {
          const adminUser = {
            ...response.data.data.user,
            role: response.data.data.user.role || 'admin',
          };

          await this.setAuthenticatedUser(
            response.data.data.token,
            adminUser,
            adminUser.role
          );

          // Always persist auth data to ensure router can access user role
          this.persistAuth();

          console.log('✅ Admin login successful');
          return { success: true, user: adminUser };
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (error) {
        console.error('Admin login error:', error);

        let errorMessage = 'Admin login failed';
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.error = errorMessage;
        return { success: false, error: errorMessage };
      } finally {
        this.loading = false;
      }
    },

    // Set authenticated user with location service initialization
    async setAuthenticatedUser(token, user, role) {
      this.token = token;
      this.user = { ...user, role };
      this.updateActivity();

      // Persist to localStorage and set axios header BEFORE setting
      // isAuthenticated=true so any reactive watches that fire immediately
      // (e.g. loadCurrency) can read the token from localStorage.
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      this.persistAuth();

      this.isAuthenticated = true;

      // Initialize location service in the background — don't block login/auth
      // so the user navigates to the dashboard immediately and the location
      // permission dialog (or EmployeeLocationNotification modal) appears there.
      this.initializeLocationService();

      // Start session monitoring when user is authenticated
      this.startSessionMonitoring();

      console.log('✅ User authenticated successfully:', {
        name: user.name,
        email: user.email,
        role: role,
        employeeId: user.employee_id || user.id,
      });
    },

    // Enhanced session extension with location context (UNCHANGED)
    async extendSession() {
      try {
        if (!this.token || !this.isAuthenticated) {
          console.warn('⚠️ Cannot extend session: not authenticated');
          return false;
        }

        console.log('🔄 Extending session...');

        // Get current location for session extension context
        let locationData = null;
        if (
          this.locationStatus.isEnabled &&
          this.locationStatus.hasPermission
        ) {
          try {
            locationData = await locationService.getCurrentLocation();
            console.log('📍 Location captured for session extension');
          } catch (error) {
            console.log('⚠️ Location unavailable for session extension');
          }
        }

        const payload = {};
        if (locationData) {
          payload.location = {
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            accuracy: locationData.accuracy,
            timestamp: locationData.timestamp,
          };
        }

        // Make request with proper headers and location data
        const response = await axios.post('/auth/sessions/extend', payload, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`,
          },
        });

        if (response.data.success && response.data.data) {
          // Update token and user data
          this.token = response.data.data.token;
          this.user = response.data.data.user;

          // Update axios default authorization header
          axios.defaults.headers.common[
            'Authorization'
          ] = `Bearer ${this.token}`;

          // Update activity timestamp
          this.updateActivity();

          // Persist to storage
          this.persistAuth();

          // Hide session warning if shown
          this.showSessionWarning = false;

          // Restart session monitoring with new token
          this.startSessionMonitoring();

          console.log('✅ Session extended successfully');
          return true;
        } else {
          throw new Error(response.data.error || 'Failed to extend session');
        }
      } catch (error) {
        console.error('❌ Session extension failed:', error);

        // If token is invalid or expired, clear auth
        if (error.response?.status === 401 || error.response?.status === 403) {
          console.warn('🔓 Token invalid during extension, logging out');
          this.clearAuth();
          if (typeof window !== 'undefined' && window.location) {
            window.location.href = '/login';
          }
        }

        this.error =
          error.response?.data?.error ||
          error.message ||
          'Session extension failed';
        return false;
      }
    },

    // Validate current session (UNCHANGED)
    async validateSession() {
      try {
        if (!this.token || !this.isAuthenticated) {
          return false;
        }

        const response = await axios.post(
          '/auth/sessions/validate',
          {},
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.token}`,
            },
          }
        );

        return response.data.success;
      } catch (error) {
        console.error('❌ Session validation failed:', error);

        if (error.response?.status === 401 || error.response?.status === 403) {
          this.clearAuth();
          if (typeof window !== 'undefined' && window.location) {
            window.location.href = '/login';
          }
        }

        return false;
      }
    },

    // Logout with location cleanup
    async logout() {
      try {
        // Stop location tracking before logout
        await this.stopLocationTracking();

        // Call logout API endpoint to invalidate session on server
        try {
          if (this.token) {
            await axios.post('/auth/logout', {}, {
              headers: {
                'Authorization': `Bearer ${this.token}`,
                'Content-Type': 'application/json'
              }
            });
            console.log('✅ Server logout successful');
          }
        } catch (apiError) {
          console.warn('⚠️ Server logout failed, proceeding with local cleanup:', apiError.message);
        }

        this.clearAuth();
        console.log('✅ User logged out successfully');
      } catch (error) {
        console.error('Logout error:', error);
        this.clearAuth();
      }
    },

    // Clear authentication with location cleanup
    clearAuth() {
      console.log('🧹 Starting complete authentication cleanup...');
      
      this.token = null;
      this.user = null;
      this.isAuthenticated = false;
      this.error = null;
      this.lastActivity = null;
      this.showCriticalLocationError = false;
      this.showLocationRequired = false;
      this.locationFailureCount = 0;

      // Stop session monitoring when clearing auth
      this.stopSessionMonitoring();

      // Stop location tracking
      this.stopLocationTracking();

      // Clear axios authorization header
      delete axios.defaults.headers.common['Authorization'];

      // Clear ALL authentication data from BOTH storage locations
      // Local Storage
      localStorage.removeItem(STORAGE_KEYS.auth.token);
      localStorage.removeItem(STORAGE_KEYS.auth.user);
      localStorage.removeItem(STORAGE_KEYS.auth.lastActivity);
      localStorage.removeItem(STORAGE_KEYS.auth.rememberMe);
      
      // Session Storage (in case app used sessionStorage)
      sessionStorage.removeItem(STORAGE_KEYS.auth.token);
      sessionStorage.removeItem(STORAGE_KEYS.auth.user);
      sessionStorage.removeItem(STORAGE_KEYS.auth.lastActivity);
      sessionStorage.removeItem(STORAGE_KEYS.auth.rememberMe);
      
      // Clear any cached data
      sessionStorage.removeItem('intended_route');
      
      
      console.log('✅ Complete authentication cleanup finished');
    },

    // ============================================================================
    // ENHANCED LOCATION SERVICE METHODS (FORCED FOR EMPLOYEES)
    // ============================================================================

    async initializeLocationService() {
      try {
        console.log('🌍 Initializing location service with company policy...');

        const initialized = await locationService.initialize({
          requestPermission: this.isEmployee, // Auto-request for employees
        });

        if (initialized) {
          this.setupLocationEventListeners();

          // For employees: MANDATORY location tracking
          if (this.isEmployee && this.locationPolicy.mandatoryForEmployees) {
            console.log(
              '🚨 Enforcing MANDATORY location tracking for employee'
            );
            await this.forceMandatoryLocationTracking();
          }

          // For admins: Optional location tracking (check saved preference)
          if (this.isAdmin) {
            const locationEnabled =
              localStorage.getItem('location_enabled') === 'true';
            if (locationEnabled) {
              await this.enableLocationTracking();
            }
          }

          console.log(
            '✅ Location service initialized with policy enforcement'
          );
        }
      } catch (error) {
        console.error('❌ Failed to initialize location service:', error);
        this.locationStatus.error = error.message;

        // For employees, this is critical
        if (this.isEmployee) {
          this.handleCriticalLocationFailure(error);
        }
      }
    },

    // NEW: Force mandatory location tracking for employees
    async forceMandatoryLocationTracking() {
      try {
        console.log('🚨 Starting MANDATORY location tracking for employee...');

        // Request permission (required for browser security)
        const hasPermission = await locationService.requestLocationPermission();

        if (!hasPermission) {
          // For employees, this is a critical error - they MUST allow location
          this.handleCriticalLocationFailure(
            new Error(
              'Location permission is required for work. Please enable in browser settings.'
            )
          );
          return false;
        }

        // Force start tracking with company policy settings
        const success = locationService.startLocationTracking({
          enableHighAccuracy: this.locationPolicy.requireHighAccuracy,
          timeout: 10000,
          maximumAge: 0, // Always get fresh location
        });

        if (success) {
          this.locationStatus.isEnabled = true;
          this.locationStatus.isTracking = true;
          this.locationStatus.hasPermission = true;
          this.locationStatus.error = null;

          // Force save as enabled (employee cannot change)
          localStorage.setItem('location_enabled', 'true');
          localStorage.setItem('location_mandatory', 'true');

          // Start server updates with policy frequency
          const sessionId = this.extractSessionId();
          locationService.startServerLocationUpdates(
            sessionId,
            this.locationPolicy.updateFrequency
          );

          console.log('✅ MANDATORY location tracking enabled for employee');
          return true;
        } else {
          throw new Error('Failed to start location tracking');
        }
      } catch (error) {
        console.error('❌ Failed to start mandatory location tracking:', error);
        this.handleCriticalLocationFailure(error);
        return false;
      }
    },

    // MODIFIED: Location tracking control (admin only)
    async enableLocationTracking() {
      try {
        // Employees cannot control location - it's mandatory
        if (this.isEmployee) {
          console.log('📍 Employee location tracking is automatically managed');
          return await this.forceMandatoryLocationTracking();
        }

        // Admins can enable/disable their own location tracking
        if (this.isAdmin) {
          console.log('🛡️ Admin enabling optional location tracking...');

          const hasPermission =
            await locationService.requestLocationPermission();

          if (!hasPermission) {
            throw new Error('Location permission denied');
          }

          const success = locationService.startLocationTracking();

          if (success) {
            this.locationStatus.isEnabled = true;
            this.locationStatus.isTracking = true;
            this.locationStatus.hasPermission = true;
            this.locationStatus.error = null;

            localStorage.setItem('location_enabled', 'true');

            const sessionId = this.extractSessionId();
            locationService.startServerLocationUpdates(sessionId);

            console.log('✅ Admin location tracking enabled');
            return true;
          }
        }

        return false;
      } catch (error) {
        console.error('❌ Failed to enable location tracking:', error);
        this.locationStatus.error = error.message;
        return false;
      }
    },

    // MODIFIED: Only admins can disable location tracking
    async disableLocationTracking() {
      try {
        // Employees CANNOT disable location tracking
        if (this.isEmployee && this.locationPolicy.mandatoryForEmployees) {
          console.warn(
            '🚨 Employees cannot disable MANDATORY location tracking'
          );
          this.locationStatus.error =
            'Location tracking is required for all employees and cannot be disabled.';
          return false;
        }

        // Only admins can disable their own location tracking
        if (this.isAdmin && this.locationPolicy.adminCanDisable) {
          console.log('🛑 Admin disabling location tracking...');

          locationService.stopLocationTracking();
          locationService.stopServerLocationUpdates();

          this.locationStatus.isEnabled = false;
          this.locationStatus.isTracking = false;
          this.locationStatus.lastKnownLocation = null;
          this.locationStatus.error = null;

          localStorage.setItem('location_enabled', 'false');

          console.log('✅ Admin location tracking disabled');
          return true;
        }

        return false;
      } catch (error) {
        console.error('❌ Failed to disable location tracking:', error);
        return false;
      }
    },

    // NEW: Handle critical location failure for employees
    handleCriticalLocationFailure(error) {
      console.error('🚨 CRITICAL location failure for employee:', error);

      this.locationFailureCount++;

      // Show persistent error notification
      this.locationStatus.error =
        'Location tracking is REQUIRED for work. Please enable location access in your browser and contact IT support if this continues.';
      this.showCriticalLocationError = true;
      this.showLocationRequired = true;

      // Log to backend for admin attention
      this.logLocationFailure(error);

      // Store in localStorage to persist across sessions
      localStorage.setItem('location_permission_required', 'true');
      localStorage.setItem(
        'location_failure_count',
        this.locationFailureCount.toString()
      );

      // If multiple failures, escalate
      if (this.locationFailureCount >= 3) {
        this.escalateLocationFailure();
      }
    },

    // NEW: Escalate repeated location failures
    escalateLocationFailure() {
      console.error('🚨 ESCALATING: Multiple location failures for employee');

      // Could potentially:
      // 1. Notify IT/Admin immediately
      // 2. Log user out until location is fixed
      // 3. Show different error message
      this.locationStatus.error =
        'CRITICAL: Location access is required for work. This has been reported to IT. Please contact support immediately.';
    },

    // NEW: Log location failure to backend
    async logLocationFailure(error) {
      try {
        await axios.post('/admin/location/failure', {
          userId: this.user?.id,
          userEmail: this.user?.email,
          error: error.message,
          timestamp: new Date().toISOString(),
          failureCount: this.locationFailureCount,
          userAgent: navigator.userAgent,
        });
      } catch (logError) {
        console.error('Failed to log location failure:', logError);
      }
    },

    // Continue with existing methods unchanged...
    setupLocationEventListeners() {
      // Listen for location updates
      locationService.addEventListener('onLocationUpdate', location => {
        this.locationStatus.lastKnownLocation = location;
        console.log('📍 Location updated:', location.locationMode);

        // Send location to server if user has active session
        if (this.isAuthenticated && this.token) {
          this.sendLocationToServer(location);
        }
      });

      // Listen for location errors
      locationService.addEventListener('onLocationError', error => {
        this.locationStatus.error = error.message;
        console.warn('⚠️ Location error:', error.message);

        // For employees, location errors are critical
        if (this.isEmployee) {
          this.handleCriticalLocationFailure(error);
        }
      });

      // Listen for permission changes
      locationService.addEventListener('onPermissionChange', status => {
        this.locationStatus.hasPermission = status === 'granted';
        console.log('🔐 Location permission changed:', status);

        if (status === 'denied' && this.isEmployee) {
          this.handleCriticalLocationFailure(
            new Error('Location permission was denied')
          );
        }
      });
    },

    async stopLocationTracking() {
      try {
        locationService.stopLocationTracking();
        locationService.stopServerLocationUpdates();

        this.locationStatus.isTracking = false;
      } catch (error) {
        console.error('Error stopping location tracking:', error);
      }
    },

    async sendLocationToServer(locationData) {
      try {
        const sessionId = this.extractSessionId();
        if (sessionId) {
          await locationService.sendLocationToServer(locationData, sessionId);
        }
      } catch (error) {
        console.warn('Failed to send location to server:', error.message);
      }
    },

    extractSessionId() {
      try {
        if (this.token) {
          const base64url = this.token.split('.')[1];
          const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
          const padded = base64 + '='.repeat((4 - base64.length % 4) % 4);
          const payload = JSON.parse(atob(padded));
          return payload.jti || payload.sessionId || null;
        }
      } catch (error) {
        console.warn('Failed to extract session ID from token');
      }
      return null;
    },

    // ============================================================================
    // EXISTING METHODS (UNCHANGED)
    // ============================================================================

    // Persist authentication to storage
    persistAuth() {
      if (this.token && this.user) {
        localStorage.setItem(STORAGE_KEYS.auth.token, this.token);
        localStorage.setItem(STORAGE_KEYS.auth.user, JSON.stringify(this.user));
        localStorage.setItem(
          STORAGE_KEYS.auth.lastActivity,
          this.lastActivity.toString()
        );
        localStorage.setItem(STORAGE_KEYS.auth.rememberMe, 'true');
      }
    },

    // Update last activity
    updateActivity() {
      this.lastActivity = Date.now();
      if (this.isAuthenticated) {
        localStorage.setItem(
          STORAGE_KEYS.auth.lastActivity,
          this.lastActivity.toString()
        );
      }
    },

    // Start session monitoring
    startSessionMonitoring() {
      // Clear any existing timers
      this.stopSessionMonitoring();

      if (!this.isAuthenticated) {
        return;
      }

      console.log('⏰ Starting session monitoring');

      // Get session timeout based on user role
      const timeout =
        this.user?.role === 'admin'
          ? this.adminSessionTimeout
          : this.sessionTimeout;

      // Set warning timer (5 minutes before session expires)
      this.sessionWarningTimer = setTimeout(() => {
        if (this.isAuthenticated) {
          console.log('⚠️ Session warning triggered');
          this.showSessionWarning = true;
        }
      }, timeout - this.sessionWarningTimeout);

      // Set session check interval to check every minute
      this.sessionCheckInterval = setInterval(() => {
        if (this.isAuthenticated && !this.isSessionValid) {
          console.log('🔒 Session expired, logging out...');
          this.logout();
        }
      }, 60000); // Check every minute
    },

    // Stop session monitoring
    stopSessionMonitoring() {
      if (this.sessionCheckInterval) {
        clearInterval(this.sessionCheckInterval);
        this.sessionCheckInterval = null;
      }

      if (this.sessionWarningTimer) {
        clearTimeout(this.sessionWarningTimer);
        this.sessionWarningTimer = null;
      }

      this.showSessionWarning = false;
      console.log('⏰ Session monitoring stopped');
    },

    // Handle session warning response
    handleSessionWarning(action) {
      if (action === 'extend') {
        this.extendSession();
      } else if (action === 'logout') {
        this.logout();
      }
      this.showSessionWarning = false;
    },

    // Refresh token (for future use)
    async refreshToken() {
      try {
        return await this.verifyToken();
      } catch (error) {
        console.error('Token refresh failed:', error);
        this.clearAuth();
        return false;
      }
    },

    // Additional helper methods
    hasPermission(permission) {
      if (this.isAdmin) return true;
      const userPermissions = this.user?.permissions || [];
      return userPermissions.includes(permission);
    },

    getDisplayName() {
      if (this.user?.name) return this.user.name;
      if (this.user?.first_name && this.user?.last_name) {
        return `${this.user.first_name} ${this.user.last_name}`;
      }
      return this.user?.email?.split('@')[0] || 'User';
    },

    canPerformFieldWork() {
      return this.isEmployee && this.canWorkField;
    },

    getDashboardRoute() {
      return this.isAdmin ? '/admin/dashboard' : '/employee/dashboard';
    },

    async forceReauth() {
      console.log('🔄 Forcing re-authentication...');
      this.clearAuth();

      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    },
  },
});

// Add axios request interceptor to always include auth headers
axios.interceptors.request.use(
  config => {
    // Get token from localStorage - try multiple possible keys
    const token = localStorage.getItem(STORAGE_KEYS.auth.token) || 
                  localStorage.getItem('auth_token') ||
                  localStorage.getItem('token') ||
                  sessionStorage.getItem(STORAGE_KEYS.auth.token) ||
                  sessionStorage.getItem('auth_token') ||
                  sessionStorage.getItem('token');
    
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('🔐 Request interceptor: Added auth token to request', {
        url: config.url,
        method: config.method,
        tokenPresent: !!token,
        tokenStart: token ? token.substring(0, 10) + '...' : 'none'
      });
    } else if (!token) {
      console.warn('⚠️ Request interceptor: No auth token found for request', {
        url: config.url,
        method: config.method
      });
    }
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Add axios response interceptor to handle auth errors globally
axios.interceptors.response.use(
  response => response,
  async error => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      // Let login and /auth/me endpoints handle their own 401s
      const isSelfHandled = url.includes('/auth/login') ||
                            url.includes('/auth/admin') ||
                            url.includes('/auth/me');
      if (!isSelfHandled) {
        console.log('🔒 Global 401 interceptor: clearing auth and redirecting');
        const authStore = useAuthStore();
        authStore.clearAuth();
        if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

console.log('✅ AUTH STORE UPDATED - FORCED Location tracking for employees!');
